// pages/TanachBookChaptersList.js
import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { fetchWithAuth } from '../auth';
import Breadcrumbs from '../components/Breadcrumbs';
import { SkeletonCardGrid } from '../components/ui';
import Accordion from '../components/Accordion';
import { useResourceDetailModal } from '../components/ResourceDetailModalContext';

export default function TanachBookChaptersList() {
  const { id } = useParams();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const res = await fetchWithAuth(`/api/tanach-books/${id}/`);
        if (!res.ok) throw new Error('Failed');
        const data = await res.json();
        if (!ignore) setBook(data);
      } catch {
        if (!ignore) setError('שגיאה בטעינת פרטי הספר');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [id]);

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="h-6 w-64 bg-secondary-200 dark:bg-secondary-700 rounded animate-pulse mb-6" />
        <div className="h-8 w-32 bg-secondary-200 dark:bg-secondary-700 rounded animate-pulse mx-auto mb-8" />
        <SkeletonCardGrid count={6} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-wrapper">
        <div className="text-center py-8">
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  if (!book) return null;

  const chapters = (book.chapters || []).filter(c => c.is_enabled);
  const generalResources = (book.general_topic_resources || []).filter(r => r.is_enabled);

  const { openResourceDetail } = useResourceDetailModal();

  const handleGeneralResourceClick = (resource) => {
    const hasFile = !!resource.file;
    const isExternal = !!resource.url && !hasFile;
    const downloadUrl = hasFile
      ? `/api/tanach-book-general-topic-resources/${resource.id}/download/`
      : resource.file || resource.url;
    if (!resource.use_detail_modal && downloadUrl) {
      window.open(downloadUrl, isExternal ? '_blank' : '_self', isExternal ? 'noopener,noreferrer' : undefined);
      return;
    }

    const modalItem = {
      id: resource.id,
      model_type: 'tanach_book_general_resource',
      title: resource.title || 'משאב ללא כותרת',
      summary: resource.explanation || '',
      explanation: resource.explanation || '',
      author: resource.author || '',
      subjectLabel: resource.discipline_name || book.name || 'תנ"ך',
      typeLabel: resource.resource_type_label || 'משאב',
      grades: resource.grades || [],
      tags: [],
      downloadUrl,
      date: resource.created_at || '',
    };
    openResourceDetail(modalItem);
  };

  return (
    <div className="page-wrapper">
      <Breadcrumbs
        items={[
          { to: '/disciplines', label: 'תחומי דעת' },
          { to: '/tanach/books', label: 'תנ"ך' },
          { label: `${book.name} - פרקים` },
        ]}
      />

      <div className="toolbar-center">
        <h2 className="text-2xl md:text-3xl font-bold text-secondary-900 dark:text-white">
          {book.name} - פרקים
        </h2>
      </div>

      <p className="text-center mt-2 mb-6">
        <Link
          to="/tanach/books"
          className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
        >
          חזרה לספרי תנ"ך
        </Link>
      </p>

      {/* General Topics Section */}
      {generalResources.length > 0 && (
        <section className="mt-8">
          <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">נושאים כלליים</h3>
          <div className="grid-autofit">
            {generalResources.map(resource => (
              <article
                key={resource.id}
                className="group bg-white dark:bg-secondary-800 rounded-2xl shadow-card overflow-hidden transition-all duration-300 hover:shadow-card-hover hover:-translate-y-1"
              >
                <button
                  type="button"
                  onClick={() => handleGeneralResourceClick(resource)}
                  className="block w-full relative overflow-hidden bg-secondary-100 dark:bg-secondary-700"
                  style={{ aspectRatio: '3/2' }}
                >
                  <div className="w-full h-full flex items-center justify-center">
                    <svg className="w-12 h-12 text-secondary-300 dark:text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                </button>
                <div className="p-4 md:p-5">
                  <h4 className="text-lg font-semibold text-secondary-900 dark:text-white text-center">
                    <button
                      type="button"
                      onClick={() => handleGeneralResourceClick(resource)}
                      className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors text-right w-full text-inherit"
                    >
                      {resource.title || 'משאב ללא כותרת'}
                    </button>
                  </h4>
                  {resource.author && <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-1">{resource.author}</p>}
                  <Accordion content={resource.explanation} className="mt-3" />
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {/* Chapters Section */}
      {chapters.length > 0 && (
        <section className="mt-8">
          <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">פרקים</h3>
          <div className="grid-autofit">
            {chapters.map(chapter => (
              <article
                key={chapter.id}
                className="group bg-white dark:bg-secondary-800 rounded-2xl shadow-card overflow-hidden transition-all duration-300 hover:shadow-card-hover hover:-translate-y-1"
              >
                <Link
                  to={`/tanach/books/${id}/chapters/${chapter.id}`}
                  className="block relative overflow-hidden bg-secondary-100 dark:bg-secondary-700"
                  style={{ aspectRatio: '3/2' }}
                >
                  {chapter.image ? (
                    <img
                      src={chapter.image}
                      alt={chapter.name}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <svg className="w-12 h-12 text-secondary-300 dark:text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}
                </Link>
                <div className="p-4 md:p-5">
                  <h4 className="text-lg font-semibold text-secondary-900 dark:text-white text-center">
                    <Link
                      to={`/tanach/books/${id}/chapters/${chapter.id}`}
                      className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                    >
                      {chapter.name}
                    </Link>
                  </h4>
                  {chapter.topics && chapter.topics.length > 0 && (
                    <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-1">{chapter.topics.length} נושאים</p>
                  )}
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {generalResources.length === 0 && chapters.length === 0 && (
        <p className="text-center text-secondary-500 dark:text-secondary-400 py-8">
          אין תוכן להצגה.
        </p>
      )}
    </div>
  );
}
